import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Speed,
  Store,
  Business,
  ConfirmationNumber,
  TrendingUp,
  Message,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import AnimatedSection from "../AnimatedSection";
import { gradients } from "../../../utils/theme";

/**
 * FeaturesSection Component
 *
 * Displays the key features of ThruChat.
 */
const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: Speed,
      title: "1-Second Entry",
      description: "QR scan parking tickets via WhatsApp",
      delay: 0,
    },
    {
      icon: Store,
      title: "Mall Discovery",
      description: "Explore nearby malls with detailed info",
      delay: 0.1,
    },
    {
      icon: Business,
      title: "Brand Navigator",
      description: "Find stores, brands & locations instantly",
      delay: 0.2,
    },
    {
      icon: ConfirmationNumber,
      title: "VIP Offers",
      description: "Exclusive deals from premium brands",
      delay: 0.3,
    },
    {
      icon: TrendingUp,
      title: "Event Updates",
      description: "Never miss sales, launches & activities",
      delay: 0.4,
    },
    {
      icon: Message,
      title: "Zero Downloads",
      description: "Everything on WhatsApp - no app needed",
      delay: 0.5,
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 6, md: 10 },
        background: "linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 6,
              background: gradients.navyLux,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Premium Features
          </Typography>
        </AnimatedSection>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <AnimatedSection delay={feature.delay}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -8 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent)",
                        transition: "left 0.5s ease",
                      },
                      "&:hover": {
                        boxShadow: "0 10px 40px rgba(255,215,0,0.15)",
                        borderColor: "#FFD700",
                        "&::before": {
                          left: "100%",
                        },
                        "& .MuiSvgIcon-root": {
                          transform: "scale(1.2) rotate(5deg)",
                          color: "#FFD700",
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <feature.icon
                        sx={{
                          fontSize: 48,
                          color: "#1a237e",
                          mb: 2,
                          transition: "all 0.3s ease",
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
